// Demo Property Service
// Handles creation and cleanup of demo data for new users

import { showingbookerAPI } from './showingbookerAPI';

// Demo property template
const DEMO_PROPERTY_TEMPLATE = {
  address_line1: "1234 Demo Street",
  unit_number: "Apt 2B",
  city: "San Francisco",
  state: "CA",
  zip_code: "94105",
  property_type: "apartment",
  bedrooms: 2,
  bathrooms: 1,
  square_feet: 950,
  rent_amount: 3200,
  
  // Agent/Owner info
  agent_name: "Sarah Johnson",
  agent_email: "demo.agent@showingbooker.com",
  agent_phone: "(555) 123-4567",
  agent_license: "DRE# 01234567",
  
  owner_type: "individual",
  owner_first_name: "Michael",
  owner_last_name: "Thompson",
  owner_email: "demo.owner@example.com",
  owner_phone: "(555) 987-6543",
  
  // Tenant info (occupied property)
  is_occupied: true,
  tenant_name: "Alex Rivera",
  tenant_email: "demo.tenant@example.com", 
  tenant_phone: "(555) 456-7890",
  
  // Property settings
  status: "listed",
  booking_approval: "automatic",
  showing_instructions: "Please remove shoes when entering. Pet-friendly building with doorman.",
  
  // Property photo (demo image)
  property_photo: "https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=800&h=600&fit=crop&crop=center&auto=format&q=80",
  
  // Mark as demo data
  is_demo: true,
  slug: `demo-${Date.now()}-${Math.random().toString(36).substring(7)}`
};

// Demo bookings templates
const DEMO_BOOKINGS_TEMPLATES = [
  // Upcoming showings
  {
    showing_date: new Date(Date.now() + 2 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 2 days from now
    showing_time: "14:00",
    buyer_name: "Jennifer Martinez",
    buyer_email: "jennifer.m@email.com",
    buyer_phone: "(555) 234-5678",
    requester_type: "buyer",
    status: "confirmed",
    confirmation_number: "DEMO001",
    is_demo: true
  },
  {
    showing_date: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 3 days from now
    showing_time: "10:30",
    buyer_name: "David Chen",
    buyer_email: "david.chen@email.com", 
    buyer_phone: "(555) 345-6789",
    buyer_agent_name: "Lisa Wong",
    buyer_agent_email: "lisa.wong@realty.com",
    buyer_agent_phone: "(555) 111-2222",
    requester_type: "buyers_agent",
    status: "confirmed",
    confirmation_number: "DEMO002",
    is_demo: true
  },
  {
    showing_date: new Date(Date.now() + 5 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 5 days from now
    showing_time: "16:30",
    buyer_name: "Robert Taylor",
    buyer_email: "rob.taylor@email.com",
    buyer_phone: "(555) 456-7890",
    requester_type: "buyer",
    status: "confirmed", 
    confirmation_number: "DEMO003",
    is_demo: true
  },
  
  // Completed showings
  {
    showing_date: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 2 days ago
    showing_time: "15:00",
    buyer_name: "Amanda Wilson",
    buyer_email: "amanda.w@email.com",
    buyer_phone: "(555) 567-8901",
    requester_type: "buyer",
    status: "completed",
    confirmation_number: "DEMO004",
    showing_feedback: "Property was beautiful! Very interested in applying.",
    is_demo: true
  },
  {
    showing_date: new Date(Date.now() - 4 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 4 days ago
    showing_time: "11:00", 
    buyer_name: "Kevin Park",
    buyer_email: "kevin.park@email.com",
    buyer_phone: "(555) 678-9012",
    buyer_agent_name: "Maria Rodriguez",
    buyer_agent_email: "maria.r@century21.com",
    buyer_agent_phone: "(555) 333-4444",
    requester_type: "buyers_agent",
    status: "completed",
    confirmation_number: "DEMO005",
    showing_feedback: "Client liked the location but looking for something larger.",
    is_demo: true
  }
];

export class DemoPropertyService {
  
  /**
   * Create complete demo property with all sample data
   * @returns {Object} Demo property data with booking links
   */
  static async createDemoProperty() {
    try {
      console.log('🎬 Creating demo property...');
      
      // 1. Create the demo property
      const demoProperty = await showingbookerAPI.createProperty(DEMO_PROPERTY_TEMPLATE);
      console.log('✅ Demo property created:', demoProperty.id);
      
      // 2. Create demo bookings for this property
      const demoBookings = [];
      for (const bookingTemplate of DEMO_BOOKINGS_TEMPLATES) {
        const booking = {
          ...bookingTemplate,
          property_id: demoProperty.id
        };
        
        try {
          const createdBooking = await showingbookerAPI.createDemoBooking(booking);
          demoBookings.push(createdBooking);
          console.log(`✅ Demo booking created: ${booking.confirmation_number}`);
        } catch (bookingError) {
          console.warn(`⚠️ Could not create demo booking ${booking.confirmation_number}:`, bookingError);
        }
      }
      
      // 3. Generate booking URL
      const bookingUrl = `${window.location.origin}/showingbooker/book/${demoProperty.slug}`;
      
      const result = {
        property: demoProperty,
        bookings: demoBookings,
        bookingUrl: bookingUrl,
        totalShowings: demoBookings.length,
        upcomingShowings: demoBookings.filter(b => b.status === 'confirmed').length,
        completedShowings: demoBookings.filter(b => b.status === 'completed').length
      };
      
      console.log('🎉 Demo property setup complete:', result);
      return result;
      
    } catch (error) {
      console.error('❌ Error creating demo property:', error);
      throw new Error(`Failed to create demo property: ${error.message}`);
    }
  }
  
  /**
   * Clean up all demo data for a user
   * @returns {Object} Cleanup summary
   */
  static async cleanupDemoData() {
    try {
      console.log('🧹 Cleaning up demo data...');
      
      // Get all demo properties for this user
      const properties = await showingbookerAPI.getProperties();
      const demoProperties = properties.filter(p => p.is_demo === true);
      
      let deletedProperties = 0;
      let deletedBookings = 0;
      
      // Delete each demo property (which should cascade delete bookings)
      for (const property of demoProperties) {
        try {
          // Get bookings for this property first (for counting)
          const bookings = await showingbookerAPI.getPropertyBookings(property.id);
          const demoBookingsCount = bookings.filter(b => b.is_demo === true).length;
          
          // Delete the property (should cascade delete bookings)
          await showingbookerAPI.deleteProperty(property.id);
          
          deletedProperties++;
          deletedBookings += demoBookingsCount;
          console.log(`✅ Deleted demo property: ${property.address_line1} (${demoBookingsCount} bookings)`);
          
        } catch (deleteError) {
          console.warn(`⚠️ Could not delete demo property ${property.id}:`, deleteError);
        }
      }
      
      const result = {
        deletedProperties,
        deletedBookings,
        success: true
      };
      
      console.log('🎉 Demo cleanup complete:', result);
      return result;
      
    } catch (error) {
      console.error('❌ Error cleaning up demo data:', error);
      throw new Error(`Failed to cleanup demo data: ${error.message}`);
    }
  }
  
  /**
   * Check if user has any demo data
   * @returns {Object} Demo data status
   */
  static async getDemoDataStatus() {
    try {
      const properties = await showingbookerAPI.getProperties();
      const demoProperties = properties.filter(p => p.is_demo === true);
      
      let totalDemoBookings = 0;
      for (const property of demoProperties) {
        try {
          const bookings = await showingbookerAPI.getPropertyBookings(property.id);
          totalDemoBookings += bookings.filter(b => b.is_demo === true).length;
        } catch (error) {
          console.warn(`Could not get bookings for demo property ${property.id}`);
        }
      }
      
      return {
        hasDemoData: demoProperties.length > 0,
        demoProperties: demoProperties.length,
        demoBookings: totalDemoBookings,
        demoProperty: demoProperties[0] || null // Return first demo property for quick access
      };
      
    } catch (error) {
      console.error('❌ Error checking demo data status:', error);
      return {
        hasDemoData: false,
        demoProperties: 0,
        demoBookings: 0,
        demoProperty: null
      };
    }
  }
}

export default DemoPropertyService;